import { FC } from 'react';
import { ProfileButton } from 'app/components/HeaderBar/ProfileButton';
import { Flex } from '@chakra-ui/react';
import { AvatarColors } from 'types/profile';
import { Platform } from 'types/general.types';
import { publicRoutes } from 'app/routing/routesConfig';
import { useIsMobile } from 'app/utils/hooks';
import { useLocation } from 'react-router-dom';
import { GlobalSearchInput } from 'app/components/GlobalSearch/GlobalSearchInput';
import { useAppSelector } from 'app/store';
import { getGlobalSearchLink } from 'app/store/GlobalSearch/selectors';
import { HelpLink } from './HelpLink';
import { HeaderLogo } from './HeaderLogo';
import { HeaderBackground } from './HeaderBackground';
import { NavControls } from './NavControls';
import { getPaddingLeft, getPaddingRight } from './utils';
import { NotificationsLinkButton } from './NotificationsButton';

interface HeaderBarComponentProps {
  isWeb?: boolean;
  isAuthenticated: boolean;
  isInitializing?: boolean;
  avatar?: string;
  signOut: () => void;
  platform?: Platform;
  initials?: string;
  colors?: AvatarColors;
  totalNotificationsCount: number;
}

export const HeaderBarComponent: FC<HeaderBarComponentProps> = ({
  isWeb = false,
  isAuthenticated,
  avatar,
  signOut,
  isInitializing,
  platform,
  initials,
  colors,
  totalNotificationsCount,
}) => {
  const { pathname } = useLocation();
  const isInPublicRoute = publicRoutes.includes(pathname);

  const isSignedOutWeb =
    (isWeb && !isAuthenticated && !isInitializing) ||
    (isInPublicRoute && isWeb);

  const renderHelpLink =
    (!isAuthenticated && !isInitializing) || isInPublicRoute;

  const isMobile = useIsMobile();
  const globalSearchLink = useAppSelector(getGlobalSearchLink);
  const globalSearchEnabled = globalSearchLink?.href && !isMobile;

  return (
    <HeaderBackground
      justifyContent={isWeb ? 'flex-end' : undefined}
      paddingLeft={getPaddingLeft(platform)}
      paddingRight={getPaddingRight(isWeb, platform)}
      isSignedOutWeb={isSignedOutWeb}
    >
      {isWeb && !isInitializing && (
        <HeaderLogo
          options={{
            isSignedOutWeb,
            isInPublicRoute,
          }}
        />
      )}
      {!isWeb && <NavControls platform={platform} />}
      {isAuthenticated && !isInPublicRoute && (
        <>
          <Flex mr={5} w="full" align="center" justify="flex-end">
            {globalSearchEnabled && <GlobalSearchInput />}
            {!isMobile && (
              <NotificationsLinkButton
                notificationsCount={totalNotificationsCount}
              />
            )}
            <ProfileButton
              avatar={avatar}
              signOut={signOut}
              initials={initials}
              colors={colors}
            />
          </Flex>
        </>
      )}
      {renderHelpLink && <HelpLink mr={isMobile ? 4 : 12} />}
    </HeaderBackground>
  );
};
